/**
 * Original quiz-show soundscape for the hot seat.
 * Synthesized with the Web Audio API — not the copyrighted TV recordings.
 */

const MUTE_KEY = "fd.millionaire.soundMuted";

function loadMuted() {
  try {
    return window.localStorage.getItem(MUTE_KEY) === "1";
  } catch {
    return false;
  }
}

function saveMuted(muted) {
  try {
    window.localStorage.setItem(MUTE_KEY, muted ? "1" : "0");
  } catch {
    /* ignore */
  }
}

function midi(n) {
  return 440 * 2 ** ((n - 69) / 12);
}

function fillPink(out) {
  let b0 = 0;
  let b1 = 0;
  let b2 = 0;
  let b3 = 0;
  let b4 = 0;
  let b5 = 0;
  let b6 = 0;
  for (let i = 0; i < out.length; i++) {
    const white = Math.random() * 2 - 1;
    b0 = 0.99886 * b0 + white * 0.0555179;
    b1 = 0.99332 * b1 + white * 0.0750759;
    b2 = 0.969 * b2 + white * 0.153852;
    b3 = 0.8665 * b3 + white * 0.3104856;
    b4 = 0.55 * b4 + white * 0.5329522;
    b5 = -0.7616 * b5 - white * 0.016898;
    out[i] = (b0 + b1 + b2 + b3 + b4 + b5 + b6 + white * 0.5362) * 0.11;
    b6 = white * 0.115926;
  }
}

class MillionaireAudio {
  constructor() {
    this.muted = loadMuted();
    this.ctx = null;
    this.master = null;
    this.sfx = null;
    this.ambience = null;
    this.reverb = null;
    this.noiseBuf = null;
    this.pinkBuf = null;
    this.bedNodes = [];
    this.bedGain = null;
    this.heartbeatTimer = null;
    this.heartbeatRunning = false;
    this.phoneNodes = [];
    this.phoneTimer = null;
    this.loadingNodes = [];
    this.lastCue = { key: "", at: 0 };
  }

  isMuted() {
    return this.muted;
  }

  setMuted(muted) {
    this.muted = Boolean(muted);
    saveMuted(this.muted);
    this.unlock();
    if (this.master) {
      const t = this.ctx.currentTime;
      this.master.gain.cancelScheduledValues(t);
      this.master.gain.setTargetAtTime(this.muted ? 0.0001 : 0.7, t, 0.03);
    }
  }

  unlock() {
    this.ensure();
    if (this.ctx?.state === "suspended") {
      this.ctx.resume().catch(() => {});
    }
  }

  ensure() {
    if (this.ctx || typeof window === "undefined") return;
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    const ctx = new AC();
    this.ctx = ctx;

    const master = ctx.createGain();
    master.gain.value = this.muted ? 0.0001 : 0.7;

    const comp = ctx.createDynamicsCompressor();
    comp.threshold.value = -16;
    comp.knee.value = 10;
    comp.ratio.value = 2.8;
    comp.attack.value = 0.008;
    comp.release.value = 0.22;

    master.connect(comp);
    comp.connect(ctx.destination);
    this.master = master;

    this.sfx = ctx.createGain();
    this.sfx.gain.value = 0.9;
    this.sfx.connect(master);

    this.ambience = ctx.createGain();
    this.ambience.gain.value = 0.85;
    this.ambience.connect(master);

    const seconds = 2.1;
    const impulse = ctx.createBuffer(2, Math.floor(ctx.sampleRate * seconds), ctx.sampleRate);
    for (let c = 0; c < 2; c++) {
      const ch = impulse.getChannelData(c);
      for (let i = 0; i < ch.length; i++) {
        ch[i] = (Math.random() * 2 - 1) * (1 - i / ch.length) ** 2.6;
      }
    }
    const conv = ctx.createConvolver();
    conv.buffer = impulse;
    const revGain = ctx.createGain();
    revGain.gain.value = 0.28;
    conv.connect(revGain);
    revGain.connect(master);
    this.reverb = conv;

    const nLen = ctx.sampleRate * 2;
    this.noiseBuf = ctx.createBuffer(1, nLen, ctx.sampleRate);
    const nd = this.noiseBuf.getChannelData(0);
    for (let i = 0; i < nLen; i++) nd[i] = Math.random() * 2 - 1;

    this.pinkBuf = ctx.createBuffer(1, nLen, ctx.sampleRate);
    fillPink(this.pinkBuf.getChannelData(0));
  }

  ready() {
    this.ensure();
    return Boolean(this.ctx);
  }

  cueOnce(key, fn, windowMs = 450) {
    const now = Date.now();
    if (this.lastCue.key === key && now - this.lastCue.at < windowMs) return;
    this.lastCue = { key, at: now };
    fn();
  }

  connectSfx(node, wet = 0.35) {
    node.connect(this.sfx);
    if (wet > 0 && this.reverb) {
      const send = this.ctx.createGain();
      send.gain.value = wet;
      node.connect(send);
      send.connect(this.reverb);
    }
  }

  envGain(t0, peak, attack, hold, release) {
    const g = this.ctx.createGain();
    g.gain.setValueAtTime(0.0001, t0);
    g.gain.exponentialRampToValueAtTime(Math.max(0.0002, peak), t0 + attack);
    g.gain.setValueAtTime(Math.max(0.0002, peak), t0 + attack + hold);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + attack + hold + release);
    return g;
  }

  osc(type, freq, t0, dur) {
    const o = this.ctx.createOscillator();
    o.type = type;
    if (typeof freq === "number") o.frequency.setValueAtTime(freq, t0);
    o.start(t0);
    o.stop(t0 + dur);
    return o;
  }

  noiseSource(buffer, t0, dur, loop = false) {
    const src = this.ctx.createBufferSource();
    src.buffer = buffer;
    src.loop = loop;
    src.start(t0);
    if (!loop) src.stop(t0 + dur);
    return src;
  }

  brass(freq, t0, dur, gain = 0.14, slideTo = null) {
    const ctx = this.ctx;
    const saw = this.osc("sawtooth", freq, t0, dur);
    const saw2 = this.osc("sawtooth", freq * 1.004, t0, dur);
    const sub = this.osc("square", freq * 0.5, t0, dur);
    const filter = ctx.createBiquadFilter();
    filter.type = "lowpass";
    filter.Q.value = 1.1;
    filter.frequency.setValueAtTime(520, t0);
    filter.frequency.exponentialRampToValueAtTime(2400, t0 + Math.min(0.06, dur * 0.2));
    filter.frequency.exponentialRampToValueAtTime(700, t0 + dur);
    if (slideTo) {
      saw.frequency.exponentialRampToValueAtTime(slideTo, t0 + dur);
      saw2.frequency.exponentialRampToValueAtTime(slideTo * 1.004, t0 + dur);
      sub.frequency.exponentialRampToValueAtTime(slideTo * 0.5, t0 + dur);
    }
    const g = this.envGain(t0, gain, 0.018, Math.max(0.02, dur * 0.45), dur * 0.5);
    saw.connect(filter);
    saw2.connect(filter);
    sub.connect(filter);
    filter.connect(g);
    this.connectSfx(g, 0.4);
  }

  sineHit(freq, t0, dur, gain = 0.2) {
    const o = this.osc("sine", freq, t0, dur);
    const g = this.envGain(t0, gain, 0.008, 0.03, dur);
    o.connect(g);
    this.connectSfx(g, 0.2);
  }

  kick(t0, gain = 0.7, startF = 118, endF = 38, dur = 0.22, dest = null) {
    const bus = dest || this.ambience || this.sfx;
    const o = this.osc("sine", startF, t0, dur);
    o.frequency.exponentialRampToValueAtTime(endF, t0 + dur * 0.55);
    const g = this.ctx.createGain();
    g.gain.setValueAtTime(gain, t0);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
    o.connect(g);
    g.connect(bus);

    const click = this.noiseSource(this.noiseBuf, t0, 0.035);
    const hp = this.ctx.createBiquadFilter();
    hp.type = "highpass";
    hp.frequency.value = 1200;
    const cg = this.envGain(t0, gain * 0.12, 0.001, 0.005, 0.03);
    click.connect(hp);
    hp.connect(cg);
    cg.connect(bus);
  }

  whoosh(t0, dur = 0.45, gain = 0.18, down = false) {
    const src = this.noiseSource(this.pinkBuf, t0, dur);
    const bp = this.ctx.createBiquadFilter();
    bp.type = "bandpass";
    bp.Q.value = 0.9;
    const a = down ? 2800 : 280;
    const b = down ? 280 : 3200;
    bp.frequency.setValueAtTime(a, t0);
    bp.frequency.exponentialRampToValueAtTime(b, t0 + dur);
    const g = this.envGain(t0, gain, 0.04, dur * 0.2, dur * 0.7);
    src.connect(bp);
    bp.connect(g);
    this.connectSfx(g, 0.15);
  }

  cymbal(t0, dur = 1.1, gain = 0.16) {
    const src = this.noiseSource(this.noiseBuf, t0, dur);
    const hp = this.ctx.createBiquadFilter();
    hp.type = "highpass";
    hp.frequency.value = 2400;
    const g = this.envGain(t0, gain, 0.005, 0.04, dur);
    src.connect(hp);
    hp.connect(g);
    this.connectSfx(g, 0.45);
  }

  chord(notes, t0, dur, gain = 0.1) {
    for (const n of notes) this.brass(midi(n), t0, dur, gain);
  }

  stopNodes(list, fade = 0.08) {
    const nodes = list.splice(0, list.length);
    if (!this.ctx || !nodes.length) return;
    const t = this.ctx.currentTime;
    for (const n of nodes) {
      try {
        if (n.gain) {
          n.gain.cancelScheduledValues(t);
          n.gain.setTargetAtTime(0.0001, t, fade);
        }
      } catch {
        /* ignore */
      }
    }
    window.setTimeout(() => {
      for (const n of nodes) {
        try {
          n.stop?.();
        } catch {
          /* already stopped */
        }
        try {
          n.disconnect?.();
        } catch {
          /* ignore */
        }
      }
    }, fade * 1000 + 80);
  }

  playContestantIntro() {
    if (!this.ready()) return;
    this.cueOnce("intro", () => {
      const t0 = this.ctx.currentTime + 0.02;
      this.whoosh(t0, 0.7, 0.14, false);
      this.kick(t0 + 0.12, 0.55, 90, 36, 0.28);
      this.chord([50, 57, 62], t0 + 0.12, 1.4, 0.09);
      this.chord([57, 62, 69], t0 + 0.55, 1.2, 0.08);
      this.chord([50, 57, 65, 69], t0 + 1.05, 1.6, 0.11);
      this.cymbal(t0 + 1.05, 1.4, 0.1);
    });
  }

  startLoadingPulse() {
    this.stopLoadingPulse();
    if (!this.ready()) return;
    const ctx = this.ctx;
    const t0 = ctx.currentTime;
    const osc = this.osc("sine", 49, t0, 600);
    const lfo = this.osc("sine", 0.7, t0, 600);
    const lfoGain = ctx.createGain();
    lfoGain.gain.value = 0.035;
    const g = ctx.createGain();
    g.gain.value = 0.05;
    lfo.connect(lfoGain);
    lfoGain.connect(g.gain);
    osc.connect(g);
    g.connect(this.ambience);
    this.loadingNodes.push(osc, lfo, g);
  }

  stopLoadingPulse() {
    this.stopNodes(this.loadingNodes, 0.2);
  }

  /** 0 easy, 1 mid, 2 hard, 3 million. */
  tier(questionIndex) {
    if (questionIndex >= 14) return 3;
    if (questionIndex >= 10) return 2;
    if (questionIndex >= 5) return 1;
    return 0;
  }

  playLetsPlay(questionIndex = 0) {
    if (!this.ready()) return;
    const tier = this.tier(questionIndex);
    this.cueOnce(`lets-${questionIndex}-${tier}`, () => this._letsPlay(tier), 400);
  }

  _letsPlay(tier) {
    const t0 = this.ctx.currentTime + 0.01;
    this.whoosh(t0, 0.35, 0.1 + tier * 0.03, false);
    this.kick(t0 + 0.05, 0.45 + tier * 0.08, 100, 34, 0.24);
    if (tier === 0) {
      this.chord([60, 64, 67], t0 + 0.06, 0.7, 0.08);
    } else if (tier === 1) {
      this.chord([55, 62, 67], t0 + 0.06, 0.85, 0.09);
      this.chord([62, 67, 74], t0 + 0.28, 0.7, 0.07);
    } else {
      this.chord([50, 56, 61], t0 + 0.05, 1.0, 0.1);
      this.chord([53, 56, 61, 68], t0 + 0.32, 0.9, 0.08);
      this.cymbal(t0 + 0.05, 0.9, 0.08);
    }
  }

  startQuestionAmbience(questionIndex = 0) {
    this.stopQuestionAmbience(0.12);
    if (!this.ready()) return;
    const ctx = this.ctx;
    const t0 = ctx.currentTime;
    const tier = this.tier(questionIndex);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, t0);
    g.gain.linearRampToValueAtTime(tier >= 2 ? 0.22 : 0.16, t0 + 0.7);
    g.connect(this.ambience);
    this.bedGain = g;

    const root = [55, 49, 41, 36][tier];
    const drone = this.osc("sine", midi(root), t0, 600);
    const drone2 = this.osc("sine", midi(root) * 1.002, t0, 600);
    const fifth = this.osc("triangle", midi(root + (tier >= 2 ? 6 : 7)), t0, 600);
    drone.connect(g);
    drone2.connect(g);

    const fifthGain = ctx.createGain();
    fifthGain.gain.value = tier === 0 ? 0.35 : tier === 1 ? 0.22 : 0.18;
    fifth.connect(fifthGain);
    fifthGain.connect(g);

    const strings = this.osc("sawtooth", midi(root + 12), t0, 600);
    const strings2 = this.osc("sawtooth", midi(root + 12) * 1.007, t0, 600);
    const lp = ctx.createBiquadFilter();
    lp.type = "lowpass";
    lp.Q.value = 0.8;
    lp.frequency.value = 280 + tier * 90;
    const lfo = this.osc("sine", tier >= 2 ? 0.11 : 0.18, t0, 600);
    const lfoG = ctx.createGain();
    lfoG.gain.value = 90 + tier * 40;
    lfo.connect(lfoG);
    lfoG.connect(lp.frequency);
    const sg = ctx.createGain();
    sg.gain.value = 0.12 + tier * 0.04;
    strings.connect(lp);
    strings2.connect(lp);
    lp.connect(sg);
    sg.connect(g);

    const pulse = this.osc("sine", tier >= 2 ? 0.85 : 1.35, t0, 600);
    const pulseG = ctx.createGain();
    pulseG.gain.value = 0.04 + tier * 0.015;
    pulse.connect(pulseG);
    pulseG.connect(g.gain);

    this.bedNodes.push(drone, drone2, fifth, fifthGain, strings, strings2, lp, lfo, sg, pulse, pulseG, g);

    if (tier >= 2) this.startHeartbeat(questionIndex);
  }

  duckAmbience(duck) {
    if (!this.bedGain || !this.ctx) return;
    const t = this.ctx.currentTime;
    const target = duck ? 0.06 : 0.18;
    this.bedGain.gain.cancelScheduledValues(t);
    this.bedGain.gain.setTargetAtTime(Math.max(0.0001, target), t, 0.12);
  }

  startHeartbeat(questionIndex) {
    this.stopHeartbeat();
    if (!this.ready()) return;
    const bpm = questionIndex >= 14 ? 54 : questionIndex >= 12 ? 64 : 74;
    const interval = 60 / bpm;
    const intensity = questionIndex >= 14 ? 1 : 0.82;
    this.heartbeatRunning = true;
    const beat = () => {
      if (!this.heartbeatRunning || !this.ctx) return;
      const t = this.ctx.currentTime;
      const dest = this.bedGain || this.ambience;
      this.kick(t, 0.72 * intensity, 105, 32, 0.2, dest);
      this.kick(t + 0.2, 0.38 * intensity, 88, 36, 0.16, dest);
    };
    beat();
    this.heartbeatTimer = window.setInterval(beat, interval * 1000);
  }

  stopHeartbeat() {
    this.heartbeatRunning = false;
    if (this.heartbeatTimer) {
      window.clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  stopQuestionAmbience(fade = 0.35) {
    this.stopHeartbeat();
    if (!this.ctx || !this.bedGain) {
      this.stopNodes(this.bedNodes, fade);
      return;
    }
    const t = this.ctx.currentTime;
    try {
      this.bedGain.gain.cancelScheduledValues(t);
      this.bedGain.gain.setTargetAtTime(0.0001, t, fade / 3);
    } catch {
      /* ignore */
    }
    this.bedGain = null;
    this.stopNodes(this.bedNodes, fade);
  }

  playLockIn() {
    if (!this.ready()) return;
    const t0 = this.ctx.currentTime;
    this.duckAmbience(true);
    this.sineHit(midi(67), t0, 0.35, 0.12);
    this.brass(midi(55), t0, 0.45, 0.08);
    const o = this.osc("triangle", midi(62), t0, 0.5);
    o.frequency.exponentialRampToValueAtTime(midi(74), t0 + 0.28);
    const g = this.envGain(t0, 0.1, 0.02, 0.12, 0.32);
    o.connect(g);
    this.connectSfx(g, 0.25);
  }

  playFinalPrompt() {
    if (!this.ready()) return;
    this.cueOnce("finalPrompt", () => {
      const t0 = this.ctx.currentTime;
      this.chord([53, 56, 60], t0, 0.7, 0.07);
      this.sineHit(midi(48), t0, 0.5, 0.1);
    }, 800);
  }

  playSuspense() {
    if (!this.ready()) return;
    this.stopQuestionAmbience(0.25);
    const t0 = this.ctx.currentTime;
    this.whoosh(t0, 0.65, 0.2, false);
    this.kick(t0 + 0.05, 0.4, 90, 40, 0.18);
    const o = this.osc("sawtooth", midi(48), t0, 0.7);
    o.frequency.exponentialRampToValueAtTime(midi(72), t0 + 0.68);
    const filter = this.ctx.createBiquadFilter();
    filter.type = "lowpass";
    filter.frequency.setValueAtTime(400, t0);
    filter.frequency.exponentialRampToValueAtTime(3200, t0 + 0.65);
    const g = this.envGain(t0, 0.12, 0.05, 0.35, 0.28);
    o.connect(filter);
    filter.connect(g);
    this.connectSfx(g, 0.35);
  }

  playCorrect(questionIndex = 0) {
    if (!this.ready()) return;
    this.cueOnce(`correct-${questionIndex}`, () => this._playCorrect(questionIndex), 600);
  }

  _playCorrect(questionIndex) {
    this.stopQuestionAmbience(0.12);
    const t0 = this.ctx.currentTime;
    const big = questionIndex >= 4;
    this.cymbal(t0, 1.3, big ? 0.18 : 0.12);
    this.kick(t0, 0.5, 140, 40, 0.2);
    this.chord([48, 52, 55, 60], t0, 0.55, 0.1);
    this.chord([52, 55, 60, 64], t0 + 0.1, 0.55, 0.09);
    this.chord([55, 60, 64, 67], t0 + 0.2, 0.7, 0.1);
    this.chord([60, 64, 67, 72], t0 + 0.34, 1.15, 0.12);
    this.sineHit(midi(84), t0 + 0.34, 1.0, 0.08);
    if (big) {
      this.chord([48, 60, 67, 72], t0 + 0.55, 1.3, 0.1);
      this.cymbal(t0 + 0.55, 1.2, 0.08);
    }
  }

  playWrong() {
    if (!this.ready()) return;
    this.cueOnce("wrong", () => this._playWrong(), 600);
  }

  _playWrong() {
    this.stopQuestionAmbience(0.08);
    const t0 = this.ctx.currentTime;
    this.cymbal(t0, 0.7, 0.1);
    this.kick(t0, 0.65, 80, 28, 0.35);
    this.brass(midi(67), t0, 1.15, 0.12, midi(43));
    this.brass(midi(68), t0, 1.15, 0.1, midi(44));
    this.brass(midi(62), t0 + 0.08, 1.1, 0.09, midi(38));
    this.chord([41, 42, 48], t0 + 0.35, 1.3, 0.1);
    this.whoosh(t0 + 0.05, 0.8, 0.12, true);
  }

  playTimeout() {
    if (!this.ready()) return;
    this.stopQuestionAmbience(0.08);
    const t0 = this.ctx.currentTime;
    const o = this.osc("square", 180, t0, 0.55);
    const g = this.envGain(t0, 0.14, 0.01, 0.35, 0.18);
    o.connect(g);
    this.connectSfx(g, 0.1);
    this.playWrong();
  }

  playWin() {
    if (!this.ready()) return;
    this.cueOnce("win", () => this._playWin(), 1200);
  }

  _playWin() {
    this.stopQuestionAmbience(0.1);
    const t0 = this.ctx.currentTime;
    this.whoosh(t0, 0.5, 0.16, false);
    this.cymbal(t0, 1.8, 0.22);
    this.kick(t0, 0.7, 150, 36, 0.28);
    const roll = [0, 0.07, 0.13, 0.18, 0.22, 0.26];
    roll.forEach((d, i) => this.kick(t0 + 0.3 + d, 0.28 + i * 0.05, 90, 40, 0.12));
    this.chord([48, 52, 55, 60], t0 + 0.55, 0.7, 0.12);
    this.chord([52, 55, 60, 64], t0 + 0.72, 0.7, 0.11);
    this.chord([55, 60, 64, 67], t0 + 0.9, 0.85, 0.12);
    this.chord([48, 60, 64, 67, 72], t0 + 1.12, 2.4, 0.14);
    this.chord([52, 67, 72, 76], t0 + 1.55, 2.0, 0.1);
    this.cymbal(t0 + 1.12, 2.0, 0.16);
    this.sineHit(midi(96), t0 + 1.2, 1.6, 0.07);
    this.sineHit(midi(100), t0 + 1.45, 1.4, 0.05);
  }

  playWalkAway() {
    if (!this.ready()) return;
    this.stopQuestionAmbience(0.2);
    const t0 = this.ctx.currentTime;
    this.chord([60, 64, 67], t0, 0.8, 0.08);
    this.chord([57, 60, 64], t0 + 0.35, 0.9, 0.08);
    this.chord([53, 57, 60], t0 + 0.75, 1.3, 0.09);
    this.sineHit(midi(41), t0 + 0.75, 1.2, 0.12);
  }

  playFiftyFifty() {
    if (!this.ready()) return;
    const t0 = this.ctx.currentTime;
    this.whoosh(t0, 0.22, 0.16, true);
    this.whoosh(t0 + 0.12, 0.22, 0.16, true);
    this.sineHit(midi(84), t0 + 0.04, 0.12, 0.08);
    this.sineHit(midi(80), t0 + 0.16, 0.12, 0.08);
  }

  playAudience() {
    if (!this.ready()) return;
    const t0 = this.ctx.currentTime;
    const src = this.noiseSource(this.pinkBuf, t0, 1.6);
    const bp = this.ctx.createBiquadFilter();
    bp.type = "bandpass";
    bp.frequency.setValueAtTime(400, t0);
    bp.frequency.linearRampToValueAtTime(1400, t0 + 0.9);
    bp.frequency.linearRampToValueAtTime(700, t0 + 1.5);
    bp.Q.value = 0.7;
    const g = this.envGain(t0, 0.14, 0.08, 0.9, 0.55);
    src.connect(bp);
    bp.connect(g);
    this.connectSfx(g, 0.2);
    for (let i = 0; i < 14; i++) {
      const tt = t0 + 0.12 + i * 0.08;
      this.sineHit(midi(72 + (i % 5)), tt, 0.06, 0.035);
    }
  }

  playPhoneRing() {
    this.stopPhoneRing();
    if (!this.ready()) return;
    const ringOnce = (t0) => {
      const mk = (freq) => {
        const o = this.osc("sine", freq, t0, 0.42);
        const g = this.envGain(t0, 0.09, 0.01, 0.32, 0.08);
        o.connect(g);
        this.connectSfx(g, 0.05);
        this.phoneNodes.push(o, g);
      };
      mk(440);
      mk(480);
    };
    const cycle = () => {
      if (!this.ctx) return;
      const t = this.ctx.currentTime;
      ringOnce(t);
      ringOnce(t + 0.5);
    };
    cycle();
    this.phoneTimer = window.setInterval(cycle, 1800);
  }

  stopPhoneRing() {
    if (this.phoneTimer) {
      window.clearInterval(this.phoneTimer);
      this.phoneTimer = null;
    }
    this.stopNodes(this.phoneNodes, 0.05);
  }

  playTick(secondsLeft) {
    if (!this.ready()) return;
    const t0 = this.ctx.currentTime;
    const freq = 880 + Math.max(0, 10 - secondsLeft) * 70;
    const o = this.osc("square", freq, t0, 0.06);
    const hp = this.ctx.createBiquadFilter();
    hp.type = "highpass";
    hp.frequency.value = 600;
    const g = this.envGain(t0, secondsLeft <= 3 ? 0.09 : 0.055, 0.001, 0.01, 0.05);
    o.connect(hp);
    hp.connect(g);
    this.connectSfx(g, 0);
  }

  playTreeCelebrate() {
    if (!this.ready()) return;
    const t0 = this.ctx.currentTime;
    this.sineHit(midi(76), t0, 0.25, 0.08);
    this.sineHit(midi(83), t0 + 0.07, 0.35, 0.09);
    this.sineHit(midi(88), t0 + 0.14, 0.5, 0.07);
  }

  stopAll() {
    this.stopLoadingPulse();
    this.stopQuestionAmbience(0.15);
    this.stopPhoneRing();
    this.stopHeartbeat();
  }
}

export const millionaireAudio = new MillionaireAudio();
